import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Slider, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Checkbox, 
  FormControlLabel, 
  Button, 
  Divider, 
  TextField,
  Card,
  CardContent,
  useTheme,
  SelectChangeEvent,
  Stack,
  Tooltip,
  IconButton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CalculateIcon from '@mui/icons-material/Calculate';
import DevicesIcon from '@mui/icons-material/Devices';
import CategoryIcon from '@mui/icons-material/Category';
import ExtensionIcon from '@mui/icons-material/Extension';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import StarIcon from '@mui/icons-material/Star';
import PageTemplate from '../components/templates/PageTemplate';
import { useLanguage } from '../context/LanguageContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PaymentIcon from '@mui/icons-material/Payment';
import BuildIcon from '@mui/icons-material/Build';

const CostSimulator: React.FC = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  
  // Helper function to convert translation value to string
  const getTranslationString = (key: string, defaultValue: string = ''): string => {
    const translation = t(key);
    if (typeof translation === 'string') {
      return translation;
    }
    if (Array.isArray(translation)) {
      return translation[0] || defaultValue;
    }
    return defaultValue;
  };
  
  // State for form inputs
  const [appCategory, setAppCategory] = useState<string>('business');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [selectedPack, setSelectedPack] = useState<string | null>(null);
  const [complexity, setComplexity] = useState<number>(50);
  const [screens, setScreens] = useState<number>(10);
  const [features, setFeatures] = useState<number>(15);
  const [authentication, setAuthentication] = useState<boolean>(false);
  const [paymentIntegration, setPaymentIntegration] = useState<boolean>(false);
  const [apiIntegration, setApiIntegration] = useState<boolean>(false);
  const [adminPanel, setAdminPanel] = useState<boolean>(false);
  const [maintenance, setMaintenance] = useState<boolean>(false);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [timeEstimate, setTimeEstimate] = useState<string>('');

  // Application categories
  const appCategories = [
    { value: 'business', label: getTranslationString('simulator.appCategories.business', 'Business Application'), description: 'CRM, ERP, Gestion interne', icon: '/resources/logo/crm.png' },
    { value: 'ecommerce', label: getTranslationString('simulator.appCategories.ecommerce', 'E-commerce'), description: 'Boutique en ligne, Marketplace', icon: '/resources/logo/cart.png' },
    { value: 'social', label: getTranslationString('simulator.appCategories.social', 'Social Network'), description: 'Communauté, Partage, Interaction', icon: '/resources/logo/social.png' },
    { value: 'utility', label: getTranslationString('simulator.appCategories.utility', 'Utility App'), description: 'Outils, Calculatrices, Trackers', icon: '/resources/logo/tools.png' },
    { value: 'entertainment', label: getTranslationString('simulator.appCategories.entertainment', 'Entertainment'), description: 'Jeux, Médias, Loisirs', icon: '/resources/logo/game.png' },
    { value: 'education', label: getTranslationString('simulator.appCategories.education', 'Education'), description: 'Formation, E-learning, Quiz', icon: '/resources/logo/education.png' }
  ];

  // Platform options with icons - Updated with tablets
  const platformOptions = [
    { value: 'web', label: 'Web', icon: '/resources/logo/web.png' },
    { value: 'windows', label: 'Windows (Desktop)', icon: '/resources/logo/windows.png' },
    { value: 'android', label: 'Android (Mobile)', icon: '/resources/logo/playstore.png' },
    { value: 'ios', label: 'iOS (Mobile)', icon: '/resources/logo/applestore.png' },
    { value: 'android_tablet', label: 'Android Tablet', icon: '/resources/logo/playstore.png' },
    { value: 'ipados', label: 'iPadOS', icon: '/resources/logo/applestore.png' }
  ];

  // Module options with categories - Reorganized according to specifications
  const moduleOptions = [
    // Essential Features
    { value: 'user_registration', label: 'User Registration', category: 'essential', icon: '/resources/logo/firebase.png', description: 'Create account functionality', cost: 800 },
    { value: 'user_dashboard', label: 'User Dashboard', category: 'essential', icon: '/resources/logo/dashboard.png', description: 'Personalized user dashboard', cost: 1200 },
    { value: 'admin_interface', label: 'Admin Interface', category: 'essential', icon: '/resources/logo/admin.png', description: 'Administration panel', cost: 2000 },
    { value: 'dark_mode', label: 'Dark/Light Mode', category: 'essential', icon: '/resources/logo/theme.png', description: 'Theme switching functionality', cost: 600 },
    
    // Authentication
    { value: 'email_auth', label: 'Email Authentication', category: 'auth', icon: '/resources/logo/firebase.png', description: 'Email/password authentication', cost: 800 },
    { value: 'social_auth', label: 'Social Login', category: 'auth', icon: '/resources/logo/facebook.png', description: 'Google, Facebook, Apple login', cost: 1000 },
    { value: 'two_factor', label: 'Two-Factor Authentication', category: 'auth', icon: '/resources/logo/2fa.png', description: '2FA security', cost: 600 },
    { value: 'role_management', label: 'Role Management', category: 'auth', icon: '/resources/logo/roles.png', description: 'User roles and permissions', cost: 1200 },
    
    // Advanced Features
    { value: 'payment_system', label: 'Payment System', category: 'advanced', icon: '/resources/logo/stripe.png', description: 'Stripe, PayPal, Apple Pay', cost: 2500 },
    { value: 'push_notifications', label: 'Push Notifications', category: 'advanced', icon: '/resources/logo/firebase.png', description: 'Real-time notifications', cost: 800 },
    { value: 'chat_system', label: 'Integrated Chat', category: 'advanced', icon: '/resources/logo/slack.png', description: 'Real-time messaging', cost: 2000 },
    { value: 'geolocation', label: 'Geolocation', category: 'advanced', icon: '/resources/logo/gps.png', description: 'Location-based features', cost: 1000 },
    { value: 'qr_scanner', label: 'QR Code Scanner', category: 'advanced', icon: '/resources/logo/qr.png', description: 'QR code reading', cost: 600 },
    { value: 'booking_system', label: 'Booking/Planning', category: 'advanced', icon: '/resources/logo/calendar.png', description: 'Appointment scheduling', cost: 1800 },
    { value: 'digital_signature', label: 'Digital Signature', category: 'advanced', icon: '/resources/logo/signature.png', description: 'Electronic signature', cost: 1200 },
    
    // Database & Cloud
    { value: 'firebase', label: 'Firebase Integration', category: 'database', icon: '/resources/logo/firebase.png', description: 'Firebase backend services', cost: 1500 },
    { value: 'supabase', label: 'Supabase Integration', category: 'database', icon: '/resources/logo/supabase.jpg', description: 'Supabase backend services', cost: 1200 },
    { value: 'postgresql', label: 'PostgreSQL Database', category: 'database', icon: '/resources/logo/sql.png', description: 'PostgreSQL database setup', cost: 1000 },
    { value: 'realtime_sync', label: 'Real-time Sync', category: 'database', icon: '/resources/logo/sync.png', description: 'Real-time data synchronization', cost: 1500 },
    { value: 'action_history', label: 'Action History', category: 'database', icon: '/resources/logo/history.png', description: 'User action tracking', cost: 800 },
    
    // E-commerce Modules
    { value: 'product_management', label: 'Product Management', category: 'ecommerce', icon: '/resources/logo/products.png', description: 'Product catalog management', cost: 2000 },
    { value: 'shopping_cart', label: 'Shopping Cart', category: 'ecommerce', icon: '/resources/logo/cart.png', description: 'Shopping cart functionality', cost: 1500 },
    { value: 'secure_payment', label: 'Secure Payment', category: 'ecommerce', icon: '/resources/logo/security.png', description: 'Secure payment processing', cost: 2000 },
    { value: 'order_tracking', label: 'Order Tracking', category: 'ecommerce', icon: '/resources/logo/tracking.png', description: 'Order status tracking', cost: 1200 },
    { value: 'auto_billing', label: 'Auto Billing', category: 'ecommerce', icon: '/resources/logo/billing.png', description: 'Automatic billing system', cost: 1800 },
    
    // Accessibility & Languages
    { value: 'multilingual', label: 'Multilingual Interface', category: 'accessibility', icon: '/resources/logo/languages.png', description: 'French, English, German, etc.', cost: 1500 },
    { value: 'wcag_compliance', label: 'WCAG Compliance', category: 'accessibility', icon: '/resources/logo/accessibility.png', description: 'Accessibility standards', cost: 2000 },
    { value: 'screen_reader', label: 'Screen Reader Support', category: 'accessibility', icon: '/resources/logo/reader.png', description: 'Screen reader compatibility', cost: 1200 },
    
    // Design & UI
    { value: 'custom_ui', label: 'Custom UI Design', category: 'design', icon: '/resources/logo/design.png', description: 'Custom user interface', cost: 3000 },
    { value: 'figma_integration', label: 'Figma Integration', category: 'design', icon: '/resources/logo/figma.png', description: 'Figma/Adobe XD integration', cost: 1000 },
    { value: 'custom_themes', label: 'Custom Themes', category: 'design', icon: '/resources/logo/themes.png', description: 'Customizable themes', cost: 1200 },
    { value: 'responsive_design', label: 'Responsive Design', category: 'design', icon: '/resources/logo/responsive.png', description: 'Mobile-responsive design', cost: 1500 },
    
    // Security & GDPR
    { value: 'cookie_consent', label: 'Cookie Consent', category: 'security', icon: '/resources/logo/cookies.png', description: 'GDPR cookie management', cost: 600 },
    { value: 'encrypted_storage', label: 'Encrypted Storage', category: 'security', icon: '/resources/logo/encryption.png', description: 'Data encryption', cost: 1000 },
    { value: 'gdpr_compliance', label: 'GDPR Compliance', category: 'security', icon: '/resources/logo/gdpr.png', description: 'GDPR compliance tools', cost: 1500 },
    { value: 'audit_logs', label: 'Audit Logs', category: 'security', icon: '/resources/logo/audit.png', description: 'Security audit logging', cost: 800 },
    
    // Analytics & Monitoring
    { value: 'analytics_dashboard', label: 'Analytics Dashboard', category: 'analytics', icon: '/resources/logo/google-analytics.png', description: 'User analytics', cost: 1200 },
    { value: 'error_tracking', label: 'Error Tracking', category: 'analytics', icon: '/resources/logo/sentry.png', description: 'Error monitoring', cost: 800 },
    { value: 'performance_monitoring', label: 'Performance Monitoring', category: 'analytics', icon: '/resources/logo/newrelic.png', description: 'Performance tracking', cost: 1000 },
    { value: 'user_behavior', label: 'User Behavior Tracking', category: 'analytics', icon: '/resources/logo/hotjar.png', description: 'User behavior analysis', cost: 1200 },
    
    // Integration & APIs
    { value: 'rest_api', label: 'REST API', category: 'integration', icon: '/resources/logo/api.png', description: 'RESTful API development', cost: 2000 },
    { value: 'graphql_api', label: 'GraphQL API', category: 'integration', icon: '/resources/logo/graphql.png', description: 'GraphQL API development', cost: 2500 },
    { value: 'webhook_system', label: 'Webhook System', category: 'integration', icon: '/resources/logo/webhook.png', description: 'Webhook integration', cost: 800 },
    { value: 'third_party_integration', label: 'Third-party Integration', category: 'integration', icon: '/resources/logo/integration.png', description: 'External service integration', cost: 1500 },
    
    // Communication
    { value: 'email_service', label: 'Email Service', category: 'communication', icon: '/resources/logo/sendgrid.png', description: 'Email notifications', cost: 800 },
    { value: 'sms_service', label: 'SMS Service', category: 'communication', icon: '/resources/logo/twilio.png', description: 'SMS notifications', cost: 1000 },
    { value: 'video_call', label: 'Video Call Integration', category: 'communication', icon: '/resources/logo/zoom.png', description: 'Video calling features', cost: 2500 },
    { value: 'file_sharing', label: 'File Sharing', category: 'communication', icon: '/resources/logo/dropbox.png', description: 'File sharing system', cost: 1200 },
    
    // Content Management
    { value: 'cms', label: 'Content Management', category: 'content', icon: '/resources/logo/wordpress.png', description: 'Content management system', cost: 2500 },
    { value: 'media_library', label: 'Media Library', category: 'content', icon: '/resources/logo/cloudinary.png', description: 'Media file management', cost: 1000 },
    { value: 'document_management', label: 'Document Management', category: 'content', icon: '/resources/logo/documents.png', description: 'Document handling', cost: 1500 },
    { value: 'blog_system', label: 'Blog System', category: 'content', icon: '/resources/logo/medium.png', description: 'Blog functionality', cost: 800 },
    
    // Social Features
    { value: 'social_sharing', label: 'Social Sharing', category: 'social', icon: '/resources/logo/twitter.png', description: 'Social media sharing', cost: 600 },
    { value: 'comment_system', label: 'Comment System', category: 'social', icon: '/resources/logo/disqus.png', description: 'User comments', cost: 800 },
    { value: 'rating_system', label: 'Rating System', category: 'social', icon: '/resources/logo/yelp.png', description: 'User ratings and reviews', cost: 1000 },
    { value: 'user_profiles', label: 'User Profiles', category: 'social', icon: '/resources/logo/profile.png', description: 'User profile management', cost: 1200 },
    
    // Maps & Location
    { value: 'maps_integration', label: 'Maps Integration', category: 'location', icon: '/resources/logo/google-maps.png', description: 'Google Maps integration', cost: 1000 },
    { value: 'location_tracking', label: 'Location Tracking', category: 'location', icon: '/resources/logo/gps.png', description: 'Real-time location tracking', cost: 1500 },
    { value: 'geofencing', label: 'Geofencing', category: 'location', icon: '/resources/logo/fence.png', description: 'Geographic boundaries', cost: 1200 },
    
    // AI & Machine Learning
    { value: 'ai_chatbot', label: 'AI Chatbot', category: 'ai', icon: '/resources/logo/chatgpt.png', description: 'Intelligent chatbot', cost: 3500 },
    { value: 'recommendation_engine', label: 'Recommendation Engine', category: 'ai', icon: '/resources/logo/ai.png', description: 'AI-powered recommendations', cost: 3000 },
    { value: 'image_recognition', label: 'Image Recognition', category: 'ai', icon: '/resources/logo/vision.png', description: 'Image analysis', cost: 2500 },
    { value: 'natural_language', label: 'Natural Language Processing', category: 'ai', icon: '/resources/logo/claude.jpg', description: 'NLP capabilities', cost: 4000 },
    
    // Development & DevOps
    { value: 'ci_cd', label: 'CI/CD Pipeline', category: 'devops', icon: '/resources/logo/github.png', description: 'Continuous integration', cost: 2000 },
    { value: 'containerization', label: 'Containerization', category: 'devops', icon: '/resources/logo/docker.png', description: 'Docker containerization', cost: 1500 },
    { value: 'cloud_deployment', label: 'Cloud Deployment', category: 'devops', icon: '/resources/logo/aws.png', description: 'Cloud infrastructure', cost: 1800 },
    { value: 'monitoring', label: 'System Monitoring', category: 'devops', icon: '/resources/logo/datadog.png', description: 'System health monitoring', cost: 1200 },
    
    // Business & Management
    { value: 'reporting_system', label: 'Reporting System', category: 'business', icon: '/resources/logo/tableau.png', description: 'Business reporting', cost: 2000 },
    { value: 'workflow_automation', label: 'Workflow Automation', category: 'business', icon: '/resources/logo/zapier.png', description: 'Process automation', cost: 2500 },
    { value: 'project_management', label: 'Project Management', category: 'business', icon: '/resources/logo/asana.png', description: 'Project tracking', cost: 2000 },
    { value: 'inventory_management', label: 'Inventory Management', category: 'business', icon: '/resources/logo/inventory.png', description: 'Stock management', cost: 1800 },
    
    // Backup & Recovery
    { value: 'backup_system', label: 'Backup System', category: 'backup', icon: '/resources/logo/backup.png', description: 'Data backup and recovery', cost: 1000 },
    { value: 'disaster_recovery', label: 'Disaster Recovery', category: 'backup', icon: '/resources/logo/recovery.png', description: 'Disaster recovery plan', cost: 2000 },
    { value: 'data_migration', label: 'Data Migration', category: 'backup', icon: '/resources/logo/migration.png', description: 'Data migration tools', cost: 1500 }
  ];

  // Predefined packs
  const predefinedPacks = [
    {
      id: 'showcase',
      name: 'App Vitrine',
      description: 'Application de présentation avec fonctionnalités de base',
      modules: ['user_registration', 'user_dashboard', 'admin_interface', 'dark_mode', 'email_auth', 'responsive_design', 'analytics_dashboard'],
      icon: '/resources/logo/web.png'
    },
    {
      id: 'ecommerce',
      name: 'App E-commerce',
      description: 'Plateforme de vente en ligne complète',
      modules: ['user_registration', 'user_dashboard', 'admin_interface', 'email_auth', 'social_auth', 'payment_system', 'product_management', 'shopping_cart', 'secure_payment', 'order_tracking', 'auto_billing', 'push_notifications', 'analytics_dashboard', 'multilingual'],
      icon: '/resources/logo/cart.png'
    },
    {
      id: 'crm',
      name: 'CRM Interne',
      description: 'Gestion de la relation client',
      modules: ['user_registration', 'user_dashboard', 'admin_interface', 'email_auth', 'role_management', 'reporting_system', 'workflow_automation', 'project_management', 'analytics_dashboard', 'audit_logs', 'backup_system'],
      icon: '/resources/logo/crm.png'
    },
    {
      id: 'social',
      name: 'Réseau Social',
      description: 'Application sociale avec interactions',
      modules: ['user_registration', 'user_dashboard', 'admin_interface', 'email_auth', 'social_auth', 'user_profiles', 'social_sharing', 'comment_system', 'rating_system', 'chat_system', 'push_notifications', 'media_library', 'analytics_dashboard'],
      icon: '/resources/logo/social.png'
    },
    {
      id: 'booking',
      name: 'Système de Réservation',
      description: 'Gestion des rendez-vous et réservations',
      modules: ['user_registration', 'user_dashboard', 'admin_interface', 'email_auth', 'booking_system', 'payment_system', 'push_notifications', 'geolocation', 'maps_integration', 'auto_billing', 'analytics_dashboard'],
      icon: '/resources/logo/calendar.png'
    }
  ];

  // Handle platform selection
  const handlePlatformToggle = (platformValue: string) => {
    setPlatforms(prev => {
      if (prev.includes(platformValue)) {
        return prev.filter(p => p !== platformValue);
      } else {
        return [...prev, platformValue];
      }
    });
  };

  // Handle module selection
  const handleModuleToggle = (moduleValue: string) => {
    setSelectedModules(prev => {
      if (prev.includes(moduleValue)) {
        return prev.filter(m => m !== moduleValue);
      } else {
        return [...prev, moduleValue];
      }
    });
  };
  
  // Handle pack selection
  const handlePackSelection = (packId: string) => {
    setSelectedPack(packId);
    const pack = predefinedPacks.find(p => p.id === packId);
    if (pack) {
      setSelectedModules(pack.modules);
    }
  };

  // Clear pack selection
  const clearPackSelection = () => {
    setSelectedPack(null);
    setSelectedModules([]);
  };
  
  // Handle changes
  const handleAppCategoryChange = (event: SelectChangeEvent) => {
    setAppCategory(event.target.value as string);
  };

  const handleComplexityChange = (_event: Event, newValue: number | number[]) => {
    setComplexity(newValue as number);
  };
  
  const handleScreensChange = (_event: Event, newValue: number | number[]) => {
    setScreens(newValue as number);
  };
  
  const handleFeaturesChange = (_event: Event, newValue: number | number[]) => {
    setFeatures(newValue as number);
  };
  
  // Calculate estimated cost
  const calculateCost = () => {
    // Base cost depending on app category
    let baseCost = 0;
    switch (appCategory) {
      case 'business':
        baseCost = 8000;
        break;
      case 'ecommerce':
        baseCost = 10000;
        break;
      case 'social':
        baseCost = 12000;
        break;
      case 'utility':
        baseCost = 7000;
        break;
      case 'entertainment':
        baseCost = 9000;
        break;
      case 'education':
        baseCost = 8500;
        break;
      default:
        baseCost = 8000;
    }
    
    // Platform multiplier
    const platformMultiplier = Math.max(1, platforms.length * 0.4);
    
    // Module costs
    let moduleCost = 0;
    selectedModules.forEach(module => {
      const moduleInfo = moduleOptions.find(m => m.value === module);
      if (moduleInfo) {
        moduleCost += moduleInfo.cost;
    }
    });
    
    // Additional features
    const authCost = authentication ? 1500 : 0;
    const paymentCost = paymentIntegration ? 2000 : 0;
    const apiCost = apiIntegration ? 2500 : 0;
    const adminCost = adminPanel ? 3000 : 0;
    const maintenanceCost = maintenance ? 5000 : 0;
    
    // Complexity factor (1 to 2)
    const complexityFactor = 1 + (complexity / 100);
    
    // Calculate total
    const total = Math.round((baseCost + moduleCost + authCost + paymentCost + apiCost + adminCost) * platformMultiplier * complexityFactor) + maintenanceCost;
    
    setTotalCost(total);
    
    // Calculate time estimate
    const baseWeeks = 4;
    const moduleWeeks = Math.ceil(selectedModules.length / 3);
    const platformWeeks = platforms.length - 1;
    const complexityWeeks = Math.floor(complexity / 20);
    const featureWeeks = Math.ceil((screens + features) / 10);
    
    const totalWeeks = baseWeeks + moduleWeeks + platformWeeks + complexityWeeks + featureWeeks;
    const months = Math.floor(totalWeeks / 4);
    const remainingWeeks = totalWeeks % 4;
    
    let timeString = '';
    if (months > 0) {
      timeString += `${months} ${getTranslationString('simulator.months', 'month')}${months > 1 ? 's' : ''}`;
    }
    if (remainingWeeks > 0) {
      if (timeString) timeString += ' ';
      timeString += `${remainingWeeks} ${getTranslationString('simulator.weeks', 'week')}${remainingWeeks > 1 ? 's' : ''}`;
    }
    
    setTimeEstimate(timeString);
  };
  
  // Calculate complexity level based on selections
  const calculateComplexityLevel = () => {
    let complexityScore = 0;
    
    // Base complexity from slider
    complexityScore += complexity / 100;
    
    // Platform complexity
    complexityScore += platforms.length * 0.2;
    
    // Module complexity
    complexityScore += selectedModules.length * 0.05;
    
    // Feature complexity
    complexityScore += features / 50;
    
    // Screen complexity
    complexityScore += screens / 20;
    
    // Additional features complexity
    if (authentication) complexityScore += 0.1;
    if (paymentIntegration) complexityScore += 0.2;
    if (apiIntegration) complexityScore += 0.2;
    if (adminPanel) complexityScore += 0.15;
    
    // Normalize to 0-100 scale
    const normalizedComplexity = Math.min(100, Math.max(0, complexityScore * 25));
    
    if (normalizedComplexity < 30) return { level: 'Simple', color: 'success.main' };
    if (normalizedComplexity < 60) return { level: 'Moderate', color: 'warning.main' };
    if (normalizedComplexity < 80) return { level: 'Complex', color: 'error.main' };
    return { level: 'Very Complex', color: 'error.dark' };
  };

  const complexityLevel = calculateComplexityLevel();
  
  // Calcul automatique du coût quand les paramètres changent
  useEffect(() => {
    calculateCost();
  }, [
    platforms, 
    selectedModules, 
    selectedPack, 
    appCategory, 
    authentication, 
    paymentIntegration, 
    apiIntegration, 
    adminPanel, 
    maintenance,
    complexity,
    screens,
    features
  ]);

  // Effet initial pour calculer le coût au chargement
  useEffect(() => {
    calculateCost();
  }, []);
  
  return (
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
        pt: 0,
        pb: 0,
        minHeight: '100vh',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/resources/scene1.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.05,
            zIndex: 0
          }
        }}
      >
      {/* Video Background */}
      <video
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0
        }}
      >
        <source src="/resources/simulator.mp4" type="video/mp4" />
        <source src="/resources/contact.mp4" type="video/mp4" />
        <source src="/resources/videospartiate.mp4" type="video/mp4" />
      </video>
      
      {/* Overlay with blur and dark effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(1, 84, 128, 0.8) 0%, rgba(2, 136, 209, 0.6) 50%, rgba(0, 0, 0, 0.7) 100%)',
          backdropFilter: 'blur(12px)',
          zIndex: 1
        }}
      />
      
      {/* Additional dark overlay for better readability */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.4)',
          zIndex: 2
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, px: 0 }}>
          <Typography
            variant="h6"
            align="center"
          color="white"
            paragraph
            sx={{
              mb: 6,
              maxWidth: '800px',
              mx: 'auto',
              fontFamily: '"Plus Jakarta Sans", sans-serif',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            fontWeight: 500
            }}
          >
          {getTranslationString('simulator.description')}
          </Typography>
          
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, px: { xs: 2, md: 4 } }}>
          <Box sx={{ flex: { md: 4 }, width: '100%' }}>
              <Paper 
                elevation={0}
                sx={{ 
                p: { xs: 2, md: 2 },
                  borderRadius: 2,
                boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(15px)',
                mb: 4,
                border: '1px solid rgba(255,255,255,0.2)',
                width: '100%',
                maxHeight: '1400px',
                overflow: 'hidden',
                '& *::-webkit-scrollbar': {
                  width: '8px',
                  borderRadius: '4px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)'
                },
                '& *::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(1, 84, 128, 0.3)',
                  borderRadius: '4px'
                },
                '& *::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: 'rgba(1, 84, 128, 0.5)'
                }
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    color: theme.palette.primary.main,
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  }}
                >
                  <CalculateIcon sx={{ mr: 1 }} />
                {getTranslationString('simulator.parameters')}
                </Typography>
                
                <Stack spacing={4}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 4 }}>
                    <Box sx={{ flex: 1 }}>
                      <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel id="app-category-label">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DevicesIcon sx={{ mr: 1, fontSize: 18 }} />
                          {getTranslationString('simulator.appCategory')}
                          </Box>
                        </InputLabel>
                        <Select
                        labelId="app-category-label"
                        value={appCategory}
                        label={getTranslationString('simulator.appCategory')}
                        onChange={handleAppCategoryChange}
                        >
                        {appCategories.map((category) => (
                          <MenuItem key={category.value} value={category.value}>
                            {category.label}
                          </MenuItem>
                        ))}
                        </Select>
                      </FormControl>
                      
                    {/* Plateformes + Packs côte à côte */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
                      {/* Plateformes */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="subtitle2" 
                          gutterBottom
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            mb: 2
                          }}
                        >
                            <CategoryIcon sx={{ mr: 1, fontSize: 18 }} />
                          {getTranslationString('simulator.platforms')}
                        </Typography>
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gridTemplateRows: 'repeat(2, 1fr)',
                          gap: 1.2,
                          p: 1,
                          border: '1px solid rgba(0,0,0,0.1)',
                                  borderRadius: 1,
                          bgcolor: 'rgba(0,0,0,0.02)',
                          width: '100%',
                          mx: 'auto'
                        }}>
                          {platformOptions.slice(0, 6).map((option) => (
                            <Box
                              key={option.value}
                              onClick={() => handlePlatformToggle(option.value)}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                p: 1,
                                borderRadius: 1,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                border: platforms.includes(option.value) 
                                  ? '2px solid #015480' 
                                  : '2px solid transparent',
                                bgcolor: platforms.includes(option.value) 
                                  ? 'rgba(1, 84, 128, 0.1)' 
                                  : 'transparent',
                                '&:hover': {
                                  bgcolor: platforms.includes(option.value) 
                                    ? 'rgba(1, 84, 128, 0.15)' 
                                    : 'rgba(0,0,0,0.05)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                }
                              }}
                            >
                              <img
                                src={option.icon}
                                alt={option.label}
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 4,
                                  marginBottom: 4,
                                  filter: platforms.includes(option.value) ? 'none' : 'grayscale(50%)'
                                }}
                              />
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  textAlign: 'center',
                                  fontSize: '0.7rem',
                                  fontWeight: platforms.includes(option.value) ? 600 : 400,
                                  color: platforms.includes(option.value) ? 'primary.main' : 'text.secondary'
                                }}
                              >
                                {option.label}
                              </Typography>
                                </Box>
                              ))}
                            </Box>
                        {platforms.length > 0 && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                            {getTranslationString('simulator.selectedPlatforms')}: {platforms.length}
                          </Typography>
                        )}
                          </Box>
                      {/* Packs prédéfinis réduits */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="subtitle2" 
                          gutterBottom
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            mb: 2
                          }}
                        >
                          <StarIcon sx={{ mr: 1, fontSize: 18 }} />
                          {getTranslationString('simulator.predefinedPacks')}
                        </Typography>
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gridTemplateRows: 'repeat(2, 1fr)',
                          gap: 0.8,
                          mb: 1.2
                        }}>
                          {predefinedPacks.map((pack) => (
                            <Box
                              key={pack.id}
                              onClick={() => handlePackSelection(pack.id)}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                p: 0.8,
                                borderRadius: 1,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                border: selectedPack === pack.id 
                                  ? '2px solid #015480' 
                                  : '2px solid rgba(0,0,0,0.1)',
                                bgcolor: selectedPack === pack.id 
                                  ? 'rgba(1, 84, 128, 0.1)' 
                                  : 'white',
                                '&:hover': {
                                  bgcolor: selectedPack === pack.id 
                                    ? 'rgba(1, 84, 128, 0.15)' 
                                    : 'rgba(0,0,0,0.02)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                                <img
                                  src={pack.icon}
                                  alt={pack.name}
                                  style={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: 2,
                                    marginRight: 4
                                  }}
                                />
                        <Typography 
                                  variant="subtitle2" 
                          sx={{ 
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    color: selectedPack === pack.id ? 'primary.main' : 'text.primary'
                          }}
                        >
                                  {pack.name}
                        </Typography>
                      </Box>
                        <Typography 
                                variant="caption" 
                          sx={{ 
                                  color: 'text.secondary',
                                  mb: 0.2,
                                  lineHeight: 1.2,
                                  fontSize: '0.6rem'
                          }}
                        >
                                {pack.description}
                        </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontWeight: 500,
                                  color: 'success.main',
                                  fontSize: '0.55rem'
                                }}
                              >
                                {pack.modules.length} modules inclus
                              </Typography>
                      </Box>
                          ))}
                        </Box>
                        {selectedPack && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={clearPackSelection}
                            sx={{ 
                              fontSize: '0.7rem',
                              py: 0.3,
                              px: 1
                            }}
                          >
                            {getTranslationString('simulator.clearPack')}
                          </Button>
                        )}
                    </Box>
                  </Box>
                  
                    {/* Additional Features - Déplacé ici */}
                    <Box sx={{ mb: 3 }}>
                    <Divider sx={{ mb: 3 }} />
                    <Typography 
                        variant="subtitle2" 
                      gutterBottom
                      sx={{ 
                        display: 'flex',
                          alignItems: 'center',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          mb: 2,
                          color: theme.palette.primary.main
                        }}
                      >
                        <IntegrationInstructionsIcon sx={{ mr: 1, fontSize: 18 }} />
                        {getTranslationString('simulator.additionalFeatures')}
                    </Typography>
                    
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        p: 1.5,
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: 1,
                        bgcolor: 'rgba(0,0,0,0.02)'
                      }}>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={authentication}
                              onChange={(e) => setAuthentication(e.target.checked)}
                              color="primary"
                                size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <SecurityIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {getTranslationString('simulator.authentication')}
                                </Typography>
                            </Box>
                          }
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={paymentIntegration}
                              onChange={(e) => setPaymentIntegration(e.target.checked)}
                              color="primary"
                                size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PaymentIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {getTranslationString('simulator.paymentIntegration')}
                                </Typography>
                            </Box>
                          }
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={apiIntegration}
                              onChange={(e) => setApiIntegration(e.target.checked)}
                              color="primary"
                                size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IntegrationInstructionsIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {getTranslationString('simulator.apiIntegration')}
                                </Typography>
                            </Box>
                          }
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={adminPanel}
                              onChange={(e) => setAdminPanel(e.target.checked)}
                              color="primary"
                                size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <AdminPanelSettingsIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {getTranslationString('simulator.adminPanel')}
                                </Typography>
                            </Box>
                          }
                        />
                      </Box>
                      <Box sx={{ width: '100%' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={maintenance}
                              onChange={(e) => setMaintenance(e.target.checked)}
                              color="primary"
                                size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <BuildIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {getTranslationString('simulator.maintenance')}
                                </Typography>
                                <Tooltip title={getTranslationString('simulator.maintenanceTooltip')}>
                                  <IconButton size="small" sx={{ ml: 0.5, p: 0.2 }}>
                                    <InfoIcon fontSize="small" sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                    
                    {/* Module Selection - Avec scrollbar */}
                    <Box sx={{ mb: 3 }}>
                      <Divider sx={{ mb: 3 }} />
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom
                sx={{
                          display: 'flex', 
                          alignItems: 'center',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          mb: 2
                        }}
                      >
                        <ExtensionIcon sx={{ mr: 1, fontSize: 18 }} />
                        {getTranslationString('simulator.modules')}
                      </Typography>
                      
                      {/* Module Categories - True 3-column full-width layout avec scrollbar */}
                      <Box sx={{ 
                        maxHeight: '1000px', 
                        overflowY: 'auto', 
                        pr: 1, 
                        pb: '300px',
                        '&::-webkit-scrollbar': {
                          width: '8px',
                          borderRadius: '4px',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(1, 84, 128, 0.3)',
                          borderRadius: '4px'
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          backgroundColor: 'rgba(1, 84, 128, 0.5)'
                        },
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(1, 84, 128, 0.3) rgba(0, 0, 0, 0.05)'
                      }}>
                        {(() => {
                          const categories = ['essential', 'auth', 'advanced', 'database', 'ecommerce', 'accessibility', 'design', 'security', 'analytics', 'integration', 'communication', 'content', 'social', 'location', 'ai', 'devops', 'business', 'backup'];
                          const rows = [];
                          for (let i = 0; i < categories.length; i += 3) {
                            rows.push(categories.slice(i, i + 3));
                          }
                          return rows.map((row, rowIndex) => (
                            <Box key={rowIndex} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, width: '100%', mb: 3 }}>
                              {row.map((category) => {
                                const categoryModules = moduleOptions.filter(module => module.category === category);
                                if (categoryModules.length === 0) return null;
                                const categoryName = getTranslationString(`simulator.categories.${category}`);
                                return (
                                  <Box key={category} sx={{ width: '100%', mb: 2 }}>
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                  fontWeight: 600,
                                        color: 'primary.main',
                                        display: 'block',
                                        mb: 1,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      {categoryName}
                                    </Typography>
                                    {/* Table Format */}
                                    <Box sx={{ 
                                      border: '1px solid rgba(0,0,0,0.1)',
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      bgcolor: 'white',
                                      width: '100%'
                                    }}>
                                      {/* Table Header */}
                                      <Box sx={{ 
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 80px',
                                        bgcolor: 'rgba(0,0,0,0.02)',
                                        borderBottom: '1px solid rgba(0,0,0,0.1)',
                                        p: 1
                                      }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                                          {getTranslationString('simulator.module')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', textAlign: 'center' }}>
                                          {getTranslationString('simulator.cost')}
                                        </Typography>
                                      </Box>
                                      {/* Table Body */}
                                      <Box sx={{ 
                                        maxHeight: '200px', 
                                        overflowY: 'auto',
                                        '&::-webkit-scrollbar': {
                                          width: '6px',
                                          borderRadius: '3px',
                                          backgroundColor: 'rgba(0, 0, 0, 0.03)'
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                          backgroundColor: 'rgba(1, 84, 128, 0.2)',
                                          borderRadius: '3px'
                                        },
                                        '&::-webkit-scrollbar-thumb:hover': {
                                          backgroundColor: 'rgba(1, 84, 128, 0.4)'
                                        },
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: 'rgba(1, 84, 128, 0.2) rgba(0, 0, 0, 0.03)'
                                      }}>
                                        {categoryModules.map((module, index) => (
                                          <Box
                                            key={module.value}
                                            onClick={() => handleModuleToggle(module.value)}
                                            sx={{
                                              display: 'grid',
                                              gridTemplateColumns: '1fr 80px',
                                              p: 1,
                                              cursor: 'pointer',
                                              transition: 'all 0.2s ease',
                                              borderBottom: index < categoryModules.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                                              bgcolor: selectedModules.includes(module.value) 
                                                ? 'rgba(1, 84, 128, 0.1)' 
                                                : 'transparent',
                  '&:hover': {
                                                bgcolor: selectedModules.includes(module.value) 
                                                  ? 'rgba(1, 84, 128, 0.15)' 
                                                  : 'rgba(0,0,0,0.02)'
                  }
                }}
              >
                                            {/* Module Info */}
                                            <Box sx={{ pr: 1 }}>
                                              <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                  fontSize: '0.7rem',
                                                  fontWeight: selectedModules.includes(module.value) ? 600 : 500,
                                                  color: selectedModules.includes(module.value) ? 'primary.main' : 'text.primary',
                                                  display: 'block',
                                                  mb: 0.5
                                                }}
                                              >
                                                {module.label}
                                              </Typography>
                                              <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                  fontSize: '0.6rem',
                                                  color: 'text.secondary',
                                                  lineHeight: 1.2,
                                                  display: '-webkit-box',
                                                  WebkitLineClamp: 2,
                                                  WebkitBoxOrient: 'vertical',
                                                  overflow: 'hidden'
                                                }}
                                              >
                                                {module.description}
                                              </Typography>
            </Box>
                                            {/* Module Cost */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                              <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                  fontSize: '0.65rem',
                                                  fontWeight: 600,
                                                  color: selectedModules.includes(module.value) ? 'primary.main' : 'success.main',
                                                  backgroundColor: selectedModules.includes(module.value) ? 'rgba(1, 84, 128, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                                                  px: 0.8,
                                                  py: 0.3,
                                                  borderRadius: 0.5,
                                                  textAlign: 'center'
                                                }}
                                              >
                                                €{module.cost.toLocaleString()}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          ));
                        })()}
                        
                        {selectedModules.length > 0 && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                            {getTranslationString('simulator.selectedModules')}: {selectedModules.length}
                          </Typography>
                        )}
                      </Box>
                      
                      {/* Indicateur de fin de liste */}
                      <Box sx={{ 
                        width: '100%', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        mt: 4,
                        mb: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                        py: 3,
                        borderRadius: 2
                      }}>
                        <Divider sx={{ width: '25%' }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.secondary', 
                            mx: 2, 
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            fontStyle: 'italic'
                          }}
                        >
                          {getTranslationString('simulator.endOfModules', 'End of modules')}
                        </Typography>
                        <Divider sx={{ width: '25%' }} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                </Stack>
                          </Paper>
            </Box>
            
          <Box sx={{ flex: { md: 1.5 } }}>
              <Card 
                sx={{ 
                  position: 'sticky',
                  top: 80,
                borderRadius: 4,
                  overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                mb: 4,
                border: '1px solid #e2e8f0'
                }}
              >
              <CardContent sx={{ p: 0 }}>
                {/* Header élégant */}
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #015480 0%, #0288d1 100%)',
                  p: 3,
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: 'white',
                      fontWeight: 700,
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      mb: 1
                    }}
                  >
                    {getTranslationString('simulator.estimation.title', 'Project Estimation')}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: 400
                    }}
                  >
                    {getTranslationString('simulator.estimation.subtitle', 'Detailed and personalized quote')}
                  </Typography>
                </Box>

                {/* Prix principal */}
                <Box sx={{ p: 4, textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      color: '#015480',
                      fontWeight: 800,
                      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                      mb: 1,
                      fontSize: { xs: '2rem', md: '2.5rem' }
                    }}
                  >
                    €{totalCost.toLocaleString()}
                  </Typography>
                  
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#64748b',
                      fontWeight: 500,
                      mb: 2
                    }}
                  >
                    {getTranslationString('simulator.estimation.estimatedTime', 'Estimated time')}: {timeEstimate}
                  </Typography>

                  {/* Indicateur de complexité */}
                  <Box sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    px: 2, 
                    py: 1, 
                    borderRadius: 2, 
                    bgcolor: complexity > 70 ? '#fef2f2' : complexity > 40 ? '#fffbeb' : '#f0fdf4',
                    border: `1px solid ${complexity > 70 ? '#fecaca' : complexity > 40 ? '#fed7aa' : '#bbf7d0'}`
                  }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      bgcolor: complexity > 70 ? '#ef4444' : complexity > 40 ? '#f59e0b' : '#22c55e',
                      mr: 1
                    }} />
                    <Typography variant="body2" sx={{ 
                      color: complexity > 70 ? '#dc2626' : complexity > 40 ? '#d97706' : '#16a34a',
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}>
                      {complexity > 70 ? getTranslationString('simulator.estimation.complexity.complex', 'Complex') : 
                       complexity > 40 ? getTranslationString('simulator.estimation.complexity.moderate', 'Moderate') : 
                       getTranslationString('simulator.estimation.complexity.simple', 'Simple')}
                    </Typography>
                  </Box>
                </Box>

                {/* Détails du projet */}
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ 
                    color: '#1e293b',
                    fontWeight: 600, 
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <AssessmentIcon sx={{ mr: 1, color: '#015480' }} />
                    {getTranslationString('simulator.estimation.configuration', 'Configuration')}
                  </Typography>
                  
                  <Box sx={{ display: 'grid', gap: 2.5 }}>
                    {/* Plateformes */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                        {getTranslationString('simulator.estimation.platforms', 'Platforms')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {platforms.slice(0, 3).map((platform) => (
                          <Box key={platform} sx={{ 
                            width: 28, 
                            height: 28, 
                            borderRadius: '6px',
                            bgcolor: '#f1f5f9',
                      display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #e2e8f0'
                          }}>
                            <img 
                              src={platformOptions.find(p => p.value === platform)?.icon} 
                              alt={platform}
                              style={{ width: 18, height: 18, borderRadius: 3 }}
                            />
                          </Box>
                        ))}
                        {platforms.length > 3 && (
                          <Box sx={{ 
                            width: 28, 
                            height: 28, 
                            borderRadius: '6px',
                            bgcolor: '#015480',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            +{platforms.length - 3}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Modules */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                        {getTranslationString('simulator.estimation.selectedModules', 'Selected modules')}
                    </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                        {selectedModules.length}
                      </Typography>
                    </Box>

                    {/* Pack */}
                    {selectedPack && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                          {getTranslationString('simulator.estimation.selectedPack', 'Selected pack')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                          {predefinedPacks.find(p => p.id === selectedPack)?.name}
                    </Typography>
                  </Box>
                    )}

                    {/* Catégorie */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                        {getTranslationString('simulator.estimation.applicationType', 'Application type')}
                    </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                        {appCategories.find(c => c.value === appCategory)?.label}
                      </Typography>
                    </Box>

                    {/* Maintenance */}
                    {maintenance && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                          {getTranslationString('simulator.estimation.maintenanceIncluded', 'Maintenance included')}
                        </Typography>
                    <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          color: '#16a34a',
                          fontSize: '0.875rem'
                        }}>
                          <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          {getTranslationString('simulator.estimation.yes', 'Yes')}
                    </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              
                {/* Métriques */}
                <Box sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#1e293b',
                    fontWeight: 600,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <TrendingUpIcon sx={{ mr: 1, color: '#015480' }} />
                    {getTranslationString('simulator.estimation.metrics', 'Metrics')}
                </Typography>
                
                  <Box sx={{ display: 'grid', gap: 3 }}>
                    {/* Complexité */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                          {getTranslationString('simulator.estimation.complexity', 'Complexity')}
                </Typography>
                        <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                          {complexity}%
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        width: '100%', 
                        height: 6, 
                        bgcolor: '#e2e8f0', 
                        borderRadius: 3,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          width: `${complexity}%`, 
                          height: '100%', 
                          bgcolor: complexity > 70 ? '#ef4444' : complexity > 40 ? '#f59e0b' : '#22c55e',
                          borderRadius: 3,
                          transition: 'width 0.5s ease'
                        }} />
                      </Box>
                    </Box>

                    {/* Écrans */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                          {getTranslationString('simulator.estimation.screenCount', 'Number of screens')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                          {screens}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        width: '100%', 
                        height: 6, 
                        bgcolor: '#e2e8f0', 
                        borderRadius: 3,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          width: `${Math.min(screens * 5, 100)}%`, 
                          height: '100%', 
                          bgcolor: '#3b82f6',
                          borderRadius: 3,
                          transition: 'width 0.5s ease'
                        }} />
                      </Box>
                    </Box>

                    {/* Fonctionnalités */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                          {getTranslationString('simulator.estimation.features', 'Features')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                          {features}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        width: '100%', 
                        height: 6, 
                        bgcolor: '#e2e8f0', 
                        borderRadius: 3,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          width: `${Math.min(features * 4, 100)}%`, 
                          height: '100%', 
                          bgcolor: '#8b5cf6',
                          borderRadius: 3,
                          transition: 'width 0.5s ease'
                        }} />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Actions */}
                <Box sx={{ p: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderColor: '#015480',
                      color: '#015480',
                      '&:hover': {
                        borderColor: '#014a6b',
                        bgcolor: 'rgba(1, 84, 128, 0.04)'
                      }
                    }}
                    startIcon={<DownloadIcon />}
                  >
                    {getTranslationString('simulator.estimation.export', 'Export')}
                  </Button>
                <Button
                  variant="contained"
                  fullWidth
                    sx={{
                      bgcolor: '#015480',
                      '&:hover': {
                        bgcolor: '#014a6b'
                      }
                    }}
                    startIcon={<SendIcon />}
                >
                    {getTranslationString('simulator.estimation.requestQuote', 'Request Quote')}
                </Button>
                </Box>
              </CardContent>
            </Card>
            </Box>
          </Box>
        </Container>
      </Box>
  );
};

export default CostSimulator; 